/**
 * What the planner's Library panel lists, before and after a search.
 *
 * The panel runs the Recipes tab's search (`useRecipeSearch`) over this pool.
 * It used to filter with `title.includes(query)` of its own, so a French query
 * never reached an English recipe's translated title, and its "Végé" chip typed
 * the word "végé" into the same substring match.
 *
 * Kept out of the component so it can be unit-tested: this repo's jest config
 * stubs `react-native`, so nothing in a `.tsx` can be executed.
 */
import type { RecipeIndexEntry } from '../../../services/unifiedRecipeService';
import {
  mapEntriesToPersonal,
  type PersonalRecipeIndexEntry,
} from '../../../services/personalRecipeIndex';
import { rankRecipesByPantryMatch } from '../../../services/pantryShelfService';
import { isBreakfastRecipe } from '../../../utils/recipeClassification';
import { addDays, parseIsoDate } from '../../../utils/localDate';
import { MEALS, dayOf, rowOf } from './constants';

/** How many "Avec vos ingrédients" rows lead the panel before the library. */
export const PICKER_INGREDIENT_ROWS = 4;

export interface PickerPoolOptions {
  /** Opened from a breakfast slot, with the Déjeuner filter still applied. */
  breakfastOnly: boolean;
  /** The cook's hard rules. The Recipes tab never offers these; neither may the planner. */
  isExcluded?: (recipe: RecipeIndexEntry) => boolean;
}

/**
 * The recipes the panel may offer, in the shape the search hook folds.
 *
 * The allergen filter is the same one the Recipes tab applies to `safeRecipes`:
 * the old drawer skipped it and offered recipes the cook had ruled out.
 */
export function buildPickerPool(
  entries: RecipeIndexEntry[],
  { breakfastOnly, isExcluded }: PickerPoolOptions,
): PersonalRecipeIndexEntry[] {
  const kept = entries.filter(
    (r) => !(isExcluded?.(r)) && (!breakfastOnly || isBreakfastRecipe(r)),
  );
  return mapEntriesToPersonal(kept);
}

export type PickerSectionId = 'ingredients' | 'library' | 'results' | 'similar';

export type PickerItem =
  | { kind: 'header'; key: string; section: PickerSectionId; count?: number }
  | { kind: 'nearMiss'; key: string }
  | { kind: 'empty'; key: string }
  | { kind: 'recipe'; key: string; section: PickerSectionId; recipe: PersonalRecipeIndexEntry };

const header = (section: PickerSectionId, count?: number): PickerItem => ({
  kind: 'header',
  key: `h:${section}`,
  section,
  ...(count != null ? { count } : {}),
});

const rows = (section: PickerSectionId, recipes: PersonalRecipeIndexEntry[]): PickerItem[] =>
  recipes.map((recipe) => ({ kind: 'recipe', key: `${section}:${recipe.id}`, section, recipe }));

/**
 * Before anything is typed: the recipes that use what is in the pantry, then
 * the rest of the library. A recipe listed in the first section is not
 * repeated in the second.
 */
export function buildIdleItems(
  pool: PersonalRecipeIndexEntry[],
  pantryNames: string[],
  ingredientRows: number = PICKER_INGREDIENT_ROWS,
): PickerItem[] {
  const withIngredients = rankRecipesByPantryMatch(pool, pantryNames).slice(0, ingredientRows);
  const shown = new Set(withIngredients.map((r) => r.id));
  const rest = pool.filter((r) => !shown.has(r.id));

  const items: PickerItem[] = [];
  if (withIngredients.length > 0) {
    items.push(header('ingredients'), ...rows('ingredients', withIngredients));
  }
  items.push(header('library', rest.length), ...rows('library', rest));
  return items;
}

/**
 * After a search. `nearMiss` is the hook's `hasNoMatch`: nothing satisfied the
 * query, so the list holds the closest matches and says so — the old drawer
 * went blank instead.
 */
export function buildResultItems(
  results: PersonalRecipeIndexEntry[],
  nearMiss: boolean,
): PickerItem[] {
  if (nearMiss) {
    return [{ kind: 'nearMiss', key: 'near-miss' }, header('similar'), ...rows('similar', results)];
  }
  // Typed but not yet submitted, and the live pass found nothing.
  if (results.length === 0) return [{ kind: 'empty', key: 'empty' }];
  return [header('results', results.length), ...rows('results', results)];
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The words that tie the panel to its slot: "Souper · mercredi 30" in the
 * header and "Mettre mercredi" on the hovered row. `weekStart` is the Monday
 * bucket the slot lives in — on the phone that is the tapped section's week,
 * not the page's.
 */
export function pickerSlotLabels(
  sk: number | null,
  weekStart: string,
  t: Translate,
  language: string,
): { context: string | null; placeOn: string | null } {
  if (sk === null || sk < 0) return { context: null, placeOn: null };
  const date = parseIsoDate(addDays(weekStart, dayOf(sk)));
  // Only the weekday comes from Intl: `{ weekday, day }` together is ordered by
  // locale, and Chrome's "en" gives "7 Wednesday".
  const weekday = date.toLocaleDateString(language, { weekday: 'long' });
  return {
    context: t('thisWeek.picker.context', {
      meal: t(MEALS[rowOf(sk)].labelKey),
      weekday,
      date: date.getDate(),
    }),
    placeOn: t('thisWeek.picker.placeOn', { day: weekday }),
  };
}
